import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';

import { EvacuationData } from '../api/types';
import { GlobalError } from '../components/GlobalError/GlobalError';
import { useHomeData } from '../components/LoadHomeData/HomeDataProvider';

import { useParams } from 'react-router-dom';
import { fetchEvacuation, fetchPeopleForEvacuation } from '../api/mobileapi';
import { EvacuationDetails } from '../components/Evacuation/EvacuationDetails';
import { EvacuationScan } from '../components/Evacuation/EvacuationScan';
import { EvacuationScannedPeople } from '../components/Evacuation/EvacuationScannedPeople';
import { useTranslations } from '../components/Translations/TranslationsProvider';
import { AdamsError } from '../utils/errors';

export const EvacuationPage: React.FC = () => {
    const { translations } = useTranslations();
    const { currentLanguage } = useHomeData();
    const { eventId } = useParams();
    const queryClient = useQueryClient();
    const eventSource = new EventSource(`${import.meta.env.VITE_BACKEND}/sse/event/${eventId}`);

    const {
        data: event,
        error: eventError,
        isLoading: isLoadingEvent,
        refetch: refetchEvacuation,
    } = useQuery<EvacuationData, AdamsError>(
        ['fetchEvacuation', eventId],
        () => fetchEvacuation({ language: currentLanguage, eventId: eventId! }),
        {
            enabled: !!eventId,
        }
    );

    const {
        data: peopleDataPaginated,
        error: peopleDataError,
        isLoading: isLoadingPeople,
        refetch: refetchPeople,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery({
        queryKey: ['fetchedScannedPeople', eventId],
        queryFn: async ({ pageParam = 1 }) => {
            const res = await fetchPeopleForEvacuation({
                language: currentLanguage,
                eventId: eventId!,
                page: pageParam,
            });
            return {
                data: res,
                nextPage: res.persons.length === 20 ? pageParam + 1 : null,
            };
        },
        getNextPageParam: (response) => response.nextPage,
        refetchInterval: 5000,
    });

    useEffect(() => {
        if (!eventId) return;
        eventSource.onmessage = async () => {
            await queryClient.invalidateQueries({
                queryKey: ['fetchedScannedPeople', eventId],
                refetchType: 'all',
            });
        };
        eventSource.onerror = (e) => console.error('SSE error:', e);
        return () => eventSource.close();
    }, [eventId, queryClient]);

    if (isLoadingEvent) {
        return <LinearProgress />;
    }

    if (eventError) {
        return <GlobalError error={eventError} translations={translations} />;
    }

    return (
        <Box
            data-testid="event-page"
            sx={{
                maxWidth: 600,
                margin: 'auto',
                p: 0.5,
            }}
        >
            <Typography variant="h5" gutterBottom>
                {translations.EVACUATION_TITLE}
            </Typography>

            <Box display="flex" alignItems="center" gap={2} my={2}>
                <Typography variant="h6">{event.eventTitle}</Typography>
                <Chip
                    label={event.eventStatus.toUpperCase()}
                    color={event.eventStatus === 'Ongoing' ? 'success' : 'default'}
                    size="small"
                    sx={{ ml: 'auto' }}
                />
            </Box>

            <EvacuationScan eventId={eventId!} refetchPeople={refetchPeople} />

            {!peopleDataError ? (
                <EvacuationScannedPeople
                    peopleDataPaginated={peopleDataPaginated}
                    isLoadingPeople={isLoadingPeople}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                />
            ) : null}

            <EvacuationDetails event={event} refetchEvacuation={refetchEvacuation} />
        </Box>
    );
};
